import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import { 
  ClockIcon, 
  FilterIcon, 
  RefreshCwIcon, 
  FileTextIcon, 
  UserIcon, 
  TagIcon,
  MenuIcon 
} from 'lucide-react';
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ChangeLogs = () => {
  const [logs, setLogs] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [indentId, setIndentId] = useState("");
  const [changeType, setChangeType] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (indentId) params.indentId = indentId;
      if (changeType) params.changeType = changeType;

      const response = await axios.get(`${API_BASE_URL}/change_log/indents/change-logs`, { params });
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching change logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const convertToIST = (utcTimestamp) => {
    return formatInTimeZone(
      new Date(utcTimestamp),
      "Asia/Kolkata",
      "yyyy-MM-dd HH:mm:ss (z)"
    );
  };

  const getChangeTypeColor = (type) => {
    switch(type) {
      case 'create': return 'bg-green-100 text-green-800';
      case 'update': return 'bg-yellow-100 text-yellow-800';
      case 'delete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderJsonData = (data) => {
    if (!data) return "N/A";
    try {
      const parsedData = JSON.parse(data);
      return (
        <button
          onClick={() => setSelectedJsonData(parsedData)}
          className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
        >
          <FileTextIcon className="mr-2 w-4 h-4" />
          View Details
        </button>
      );
    } catch {
      return data;
    }
  };

  const closeJsonModal = () => {
    setSelectedJsonData(null);
  };

  const FilterSection = () => (
    <div className={`
      ${mobileFiltersOpen ? 'block' : 'hidden'} 
      md:block bg-white shadow-md rounded-lg mb-6 p-6
    `}>
      <div className="flex items-center mb-4">
        <FilterIcon className="mr-3 w-6 h-6 text-gray-600" />
        <h2 className="text-xl font-semibold text-gray-700">Filters</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { 
            label: "Start Date", 
            type: "date", 
            value: startDate, 
            onChange: (e) => setStartDate(e.target.value),
            icon: <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          },
          { 
            label: "End Date", 
            type: "date", 
            value: endDate, 
            onChange: (e) => setEndDate(e.target.value),
            icon: <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          },
          { 
            label: "Indent ID", 
            type: "number", 
            placeholder: "Enter Indent ID",
            value: indentId, 
            onChange: (e) => setIndentId(e.target.value),
            icon: <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          },
          { 
            label: "Change Type", 
            type: "select",
            options: [
              { value: "", label: "All Types" },
              { value: "create", label: "Create" },
              { value: "update", label: "Update" },
              { value: "delete", label: "Delete" }
            ],
            value: changeType, 
            onChange: (e) => setChangeType(e.target.value),
            icon: <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          }
        ].map((filter, index) => (
          <div key={index} className="relative">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              {filter.label}
            </label>
            {filter.type === 'select' ? (
              <div className="relative">
                {filter.icon}
                <select
                  value={filter.value}
                  onChange={filter.onChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {filter.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="relative">
                {filter.icon}
                <input
                  type={filter.type}
                  placeholder={filter.placeholder}
                  value={filter.value}
                  onChange={filter.onChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Dashboard Header */}
        <div className="bg-white shadow-md rounded-lg mb-6 p-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center mb-4 md:mb-0">
              <TagIcon className="mr-3 w-6 md:w-8 h-6 md:h-8 text-blue-600" />
              Change Logs Dashboard
            </h1>
            <div className="flex items-center space-x-2">
              {/* Mobile Filter Toggle */}
              <button 
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                className="md:hidden bg-blue-500 text-white p-2 rounded"
              >
                <MenuIcon className="w-5 h-5" />
              </button>
              <button
                onClick={fetchLogs}
                disabled={loading}
                className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300 disabled:opacity-50"
              >
                <RefreshCwIcon className="mr-2 w-4 h-4" />
                {loading ? "Refreshing..." : "Refresh Logs"}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Filters */}
        <FilterSection />

        {/* Logs Table */}
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                {[
                  "Log ID", "Indent ID", "User", "Change Type", 
                  "Original Data", "Updated Data", "Item Data", "Created At (IST)"
                ].map((header, index) => (
                  <th 
                    key={index} 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center p-4 text-gray-500">
                    Loading logs...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center p-4 text-gray-500">
                    No logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{log.id}</td>
                    <td className="px-4 py-3">{log.indent_id}</td>
                    <td className="px-4 py-3 flex items-center">
                      <UserIcon className="mr-2 w-4 h-4 text-gray-500" />
                      {log.user_name}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getChangeTypeColor(log.change_type)}`}>
                        {log.change_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">{renderJsonData(log.original_data)}</td>
                    <td className="px-4 py-3">{renderJsonData(log.updated_data)}</td>
                    <td className="px-4 py-3">{renderJsonData(log.item_data)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <ClockIcon className="mr-2 w-4 h-4 inline-block" />
                      {convertToIST(log.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* JSON Data Modal */}
        {selectedJsonData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-[95%] md:w-full max-h-[90vh]">
              <div className="flex justify-between items-center p-4 md:p-6 border-b">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">JSON Details</h2>
                <button
                  onClick={closeJsonModal}
                  className="text-red-500 hover:text-red-700 focus:outline-none"
                >
                  Close
                </button>
              </div>
              <div className="p-4 md:p-6 overflow-auto">
                <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-xs md:text-sm">
                  {JSON.stringify(selectedJsonData, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ChangeLogs;